import { useCallback, useEffect, useRef, useState } from "react";
import shortid from "shortid";

import { safeLocalStorage } from "@examspark/common-app";

const emitter = new EventTarget();

type UseState<TValue> = [value: TValue, setValue: (value: TValue) => void];

class LocalStorageChangedEvent extends Event {
  constructor(
    key: string,
    public originator: string,
    eventInitDict?: EventInit,
  ) {
    super(`change:${key}`, eventInitDict);
  }
}

const getStoredValue = <TValue>(
  key: string | undefined,
  initialValue: TValue | undefined,
) => {
  if (key == null) return initialValue;
  return safeLocalStorage.parseItem<TValue>(key) ?? initialValue;
};

export function useLocalStorage<TValue = any>(
  key: string | undefined,
  initialValue: TValue,
): UseState<TValue>;
export function useLocalStorage<TValue = any>(
  key: string | undefined,
): UseState<TValue | undefined>;
export function useLocalStorage<TValue = any>(
  key: string | undefined,
  initialValue?: TValue,
): UseState<TValue | undefined> {
  const [value, setValueInternal] = useState<TValue | undefined>(
    getStoredValue(key, initialValue),
  );
  const { current: originator } = useRef(shortid());

  const onLocalStorageChange = useCallback(
    (e: Event) => {
      //
      // If this instance of the hook is who made the change, we already set the value
      //
      if ("originator" in e && e.originator === originator) return;
      setValueInternal(getStoredValue(key, initialValue));
    },
    [originator, initialValue, key],
  );

  useEffect(() => {
    if (key == null) return;

    emitter.addEventListener(`change:${key}`, onLocalStorageChange);
    return () => {
      emitter.removeEventListener(`change:${key}`, onLocalStorageChange);
    };
  }, [key, onLocalStorageChange]);

  const setValue = useCallback(
    (newValue: TValue | undefined) => {
      setValueInternal(newValue);
      if (key == null) return;
      safeLocalStorage.serializeItem(key, newValue);
      emitter.dispatchEvent(new LocalStorageChangedEvent(key, originator));
    },
    [key, originator],
  );

  return [value, setValue];
}
